import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi, normalizeList } from '../../../lib/admin-api/index.js';
import { mapCommentsResponse, mapTicketDetailsResponse } from '../dto/support-ticket-details.dto.js';

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function mapAdminProfileOptions(payload) {
  return normalizeList(payload)
    .map((profile, index) => {
      const id = String(pickFirst(profile?.uid, profile?.id) || '').trim();
      if (!id) return null;
      const firstName = pickFirst(profile?.firstName, profile?.first_name);
      const lastName = pickFirst(profile?.lastName, profile?.last_name);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallback = pickFirst(profile?.name, profile?.email, `Profile ${index + 1}`);
      return { id, label: fullName || String(fallback || '').trim() || id };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function useSupportTicketDetails(uid) {
  const { token } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Status change
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [statusError, setStatusError] = useState('');

  // Comment creation
  const [commentBody, setCommentBody] = useState('');
  const [commentIsInternal, setCommentIsInternal] = useState(false);
  const [commentSetPendingClient, setCommentSetPendingClient] = useState(false);
  const [commentFile, setCommentFile] = useState(null);
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');

  // Assign modal
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [adminProfileOptions, setAdminProfileOptions] = useState([]);
  const [isAdminProfilesLoading, setIsAdminProfilesLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');

  const loadTicket = useCallback(async () => {
    if (!uid) return;
    setIsLoading(true);
    setError('');

    try {
      const [ticketResponse, commentsResponse] = await Promise.all([
        adminApi.getSupportTicket(token, uid),
        adminApi.getSupportTicketComments(token, uid),
      ]);
      setTicket(mapTicketDetailsResponse(ticketResponse));
      setComments(mapCommentsResponse(commentsResponse));
    } catch (requestError) {
      setError(requestError.message || 'Failed to load ticket');
    } finally {
      setIsLoading(false);
    }
  }, [token, uid]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const changeStatus = async (status) => {
    setIsStatusChanging(true);
    setStatusError('');
    try {
      const response = await adminApi.updateSupportTicketStatus(token, uid, status);
      setTicket(mapTicketDetailsResponse(response));
    } catch (requestError) {
      setStatusError(requestError.message || 'Failed to update status');
    } finally {
      setIsStatusChanging(false);
    }
  };

  const submitComment = async () => {
    if (!commentBody.trim()) return;

    setIsCommentSubmitting(true);
    setCommentError('');

    try {
      const formData = new FormData();
      formData.append('body', commentBody.trim());
      formData.append('isInternal', commentIsInternal ? '1' : '0');
      formData.append('setPendingClient', commentSetPendingClient ? '1' : '0');
      if (commentFile) {
        formData.append('file', commentFile);
      }

      // Use fetch directly for multipart (adminApi methods use JSON by default)
      const response = await fetch(
        adminApi.client.resolveUrl(`/admin/support/ticket/${encodeURIComponent(uid)}/comment`),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      const rawBody = await response.text();
      let parsedBody = null;
      if (rawBody) {
        try { parsedBody = JSON.parse(rawBody); } catch { parsedBody = rawBody; }
      }

      if (!response.ok || parsedBody?.result === false) {
        throw new Error(parsedBody?.message || `Request failed with status ${response.status}`);
      }

      setComments(mapCommentsResponse(parsedBody));
      setCommentBody('');
      setCommentIsInternal(false);
      setCommentSetPendingClient(false);
      setCommentFile(null);

      // Reload ticket to get updated status
      const ticketResponse = await adminApi.getSupportTicket(token, uid);
      setTicket(mapTicketDetailsResponse(ticketResponse));
    } catch (requestError) {
      setCommentError(requestError.message || 'Failed to send comment');
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const openAssignModal = async () => {
    setSelectedManagerId(String(ticket?.assignedId || '').trim());
    setAssignmentError('');
    setIsAssignModalOpen(true);
    setIsAdminProfilesLoading(true);

    try {
      const response = await adminApi.getProfiles(token, { isAdmin: true, limit: 100 });
      setAdminProfileOptions(mapAdminProfileOptions(response));
    } catch {
      setAdminProfileOptions([]);
    } finally {
      setIsAdminProfilesLoading(false);
    }
  };

  const closeAssignModal = () => {
    if (isAssignmentSaving) return;
    setIsAssignModalOpen(false);
    setSelectedManagerId('');
    setAssignmentError('');
  };

  const assignManager = async () => {
    const managerId = String(selectedManagerId || '').trim();
    if (!managerId) {
      setAssignmentError('Select a manager to assign');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.assignSupportTicket(token, uid, managerId);
      closeAssignModal();
      await loadTicket();
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to assign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  const downloadFile = useCallback(async (fileUid, fileName) => {
    try {
      const response = await fetch(
        adminApi.client.resolveUrl(`/admin/support/file/${encodeURIComponent(fileUid)}`),
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // non-critical
    }
  }, [token]);

  const unassignManager = async () => {
    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.unassignSupportTicket(token, uid);
      closeAssignModal();
      await loadTicket();
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to unassign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  return {
    ticket,
    comments,
    isLoading,
    error,
    isStatusChanging,
    statusError,
    changeStatus,
    commentBody,
    commentIsInternal,
    commentSetPendingClient,
    commentFile,
    isCommentSubmitting,
    commentError,
    onCommentBodyChange: (value) => setCommentBody(value),
    onCommentIsInternalChange: (value) => setCommentIsInternal(value),
    onCommentSetPendingClientChange: (value) => setCommentSetPendingClient(value),
    onCommentFileChange: (file) => setCommentFile(file),
    submitComment,
    isAssignModalOpen,
    adminProfileOptions,
    isAdminProfilesLoading,
    selectedManagerId,
    isAssignmentSaving,
    assignmentError,
    openAssignModal,
    closeAssignModal,
    onSelectManager: (id) => setSelectedManagerId(String(id || '').trim()),
    assignManager,
    unassignManager,
    reload: loadTicket,
    downloadFile,
  };
}
